import React, { useState } from 'react';
import { ToolTip, type TooltipPlacementType } from '@xarvio/tooltip';
import { Box } from '@xarvio/layout';

import { Selectable } from '../Selectable/Selectable';
import { hasMultipleLanguages } from '@xarvio/frontend-core-state/lib/auth/utils/hasMultipleLanguages';
import { supportedCountries } from '@xarvio/frontend-core-platform/lib/country/CountryUtils';
import type { SupportedCountries } from '@xarvio/frontend-core-platform/lib/country/CountryUtils';

import { CountryLanguageItem } from '../CountryLanguageItem/CountryLanguageItem';
import { useComponentVisible } from './useComponentVisible';
import styles from './CountryLanguageSwitcher.module.scss';
import { ButtonProps } from '@xarvio/button';

interface Props {
  country: string;
  language: string;
  onLocaleChange: (locale: string) => void;
  button: React.ReactElement<ButtonProps>;
  menuPlacement?: TooltipPlacementType;
  enabledCountries?: SupportedCountries[];
}

export const CountryLanguageSwitcher: React.FC<
  React.PropsWithChildren<Props>
> = ({
  country,
  language,
  onLocaleChange,
  button,
  menuPlacement = 'right',
  enabledCountries,
}) => {
  const [selectedLocale, setSelectedLocale] = useState<string>(
    `${language}_${country}`,
  );
  const { ref, isVisible, setIsVisible } =
    useComponentVisible<HTMLDivElement>(false);

  const handleSelect = (locale: string) => {
    setSelectedLocale(locale);
    onLocaleChange(locale);
    setIsVisible(false);
  };

  const handleSetIsVisible = () => {
    setIsVisible(true);
  };

  const filteredSupportedCountries = enabledCountries ?? supportedCountries;

  if (filteredSupportedCountries.length <= 1) {
    return button;
  }

  return (
    <ToolTip
      placement={menuPlacement}
      popupContent={
        <div ref={ref}>
          <Box
            width={235}
            hpadding={'no'}
            vpadding={'no'}
            className={styles.box}
          >
            {filteredSupportedCountries.map((item) => {
              return (
                <Selectable
                  key={item.locale}
                  selected={
                    item.locale === selectedLocale &&
                    item.countryCode === country
                  }
                  onClick={() => handleSelect(item.locale)}
                >
                  <CountryLanguageItem
                    showLanguage={hasMultipleLanguages(item.countryCode)}
                    languageOnRight
                    size={'small'}
                    countryTranslationKey={item.countryTranslationKey}
                    languageTranslationKey={item.languageTranslationKey}
                    countryCode={item.countryCode}
                  />
                </Selectable>
              );
            })}
          </Box>
        </div>
      }
      trigger={'click'}
      className={styles.tooltip}
      visible={isVisible}
    >
      {React.cloneElement<ButtonProps>(button, { onClick: handleSetIsVisible })}
    </ToolTip>
  );
};
